import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import {
  CalendarClock, ChevronLeft, ChevronRight, Clock, Download, Pencil, Plus, Search, Trash2,
  Upload, Users, PartyPopper, RotateCw, Radio,
} from 'lucide-react'
import {
  ConfirmDialog, DateValue, EmptyState, ErrorState, FilterBar, FormDialog, ImportDialog,
  PageHeader, PermissionGate,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useDebounce } from '@/hooks/useDebounce'
import {
  useHolidayMutations, useHolidays, useRosterMutations, useRosters, useShifts,
} from '../api'
import { ShiftForm } from '../components/ShiftForm'
import { AssignRosterForm } from '../components/AssignRosterForm'
import { shortDayName, type HolidayView, type ProcessingResult, type ShiftView } from '../types'
import { useLocations } from '@/features/org'
import { bulkPaths } from '@/lib/bulk'
import { downloadFile, toApiProblem } from '@/lib/api/client'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

type Tab = 'assignments' | 'shifts' | 'holidays'

/** /attendance/holidays is its own menu entry, so the path decides which tab opens. */
function tabFor(pathname: string): Tab {
  return pathname.endsWith('/holidays') ? 'holidays' : 'assignments'
}

const PAGE_SIZE = 25

function hours(minutes: number): string {
  return `${(minutes / 60).toFixed(minutes % 60 === 0 ? 0 : 1)}h`
}

/**
 * Who works when.
 *
 * <p>The roster is the input attendance is judged against: a person's shift decides when
 * they are late, whether a day is a week off, and how much of it is overtime. So the
 * assignment list leads, and shift patterns sit behind it.
 */
export function RosterPage() {
  // The tab comes from the path, like every other screen with siblings: /attendance/holidays
  // is a menu entry, and landing on it showing the roster is the sort of fault people work
  // around by clicking twice for a year without reporting it.
  const { pathname } = useLocation()
  const [tab, setTab] = useState<Tab>(tabFor(pathname))
  // Adjusted during render rather than in an effect: navigating from /attendance/roster to
  // /attendance/holidays must not paint the roster first and then correct itself.
  const [lastPath, setLastPath] = useState(pathname)
  if (lastPath !== pathname) {
    setLastPath(pathname)
    setTab(tabFor(pathname))
  }
  const [showShiftForm, setShowShiftForm] = useState(false)
  const [editingShift, setEditingShift] = useState<ShiftView | null>(null)
  const [removingShift, setRemovingShift] = useState<ShiftView | null>(null)
  const [holidayDraft, setHolidayDraft] = useState<
    { id?: string; holidayDate: string; name: string; locationId: string; optional: boolean } | null
  >(null)
  const [removingHoliday, setRemovingHoliday] = useState<HolidayView | null>(null)
  const [importingWhat, setImportingWhat] = useState<'shifts' | 'holidays' | 'rosters' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [showAssignForm, setShowAssignForm] = useState(false)
  const [showReprocess, setShowReprocess] = useState(false)
  const [reprocessed, setReprocessed] = useState<ProcessingResult | null>(null)
  const [reprocessFrom, setReprocessFrom] = useState('')
  const [reprocessTo, setReprocessTo] = useState('')
  const [search, setSearch] = useState('')
  const [shiftFilter, setShiftFilter] = useState('')
  const [page, setPage] = useState(0)

  // The roster is company-wide, so it is fetched a page at a time and the search runs on
  // the server: filtering a single page in the browser would quietly hide matches.
  const debouncedSearch = useDebounce(search, 300)

  const shifts = useShifts()
  const rosters = useRosters({ q: debouncedSearch, shiftId: shiftFilter, page, size: PAGE_SIZE })
  // Next year's calendar is planned in this one, so the year is a control rather than a
  // constant. The screen showed the current year and nothing else, which meant a holiday for
  // January could not be entered in December.
  const [holidayYear, setHolidayYear] = useState(() => new Date().getFullYear())
  const holidays = useHolidays(holidayYear)
  const holidayActions = useHolidayMutations()
  const locations = useLocations()
  const actions = useRosterMutations()

  const shiftList = shifts.data ?? []
  // From the navigation tree, so a menu change cannot leave the breadcrumb describing a
  // shape the product no longer has.
  const breadcrumb = useBreadcrumb()

  return (
    <div className="space-y-4">
      <PageHeader
        title="Roster"
        subtitle="Shift patterns and who is working them"
        breadcrumb={breadcrumb}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/attendance/devices">
                <Radio aria-hidden="true" />
                Devices
              </Link>
            </Button>
            <PermissionGate permission="attendance:write">
              <Button variant="outline" onClick={() => setShowReprocess((open) => !open)}>
                <RotateCw aria-hidden="true" />
                Reprocess attendance
              </Button>
            </PermissionGate>
          </div>
        }
      />

      {showReprocess && (
        <Card className="p-5">
          <h3 className="text-base font-semibold text-[var(--color-heading)]">Reprocess a date range</h3>
          <p className="mt-1 max-w-prose text-sm text-[var(--color-body)]">
            Attendance is recalculated overnight. Run it by hand after correcting a roster or a
            device mapping — processing replaces each day rather than adding to it, so running it
            twice changes nothing.
          </p>
          <form
            className="mt-4 flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              actions.reprocess.mutate(
                { from: reprocessFrom, to: reprocessTo },
                { onSuccess: setReprocessed },
              )
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="reprocess-from">From</Label>
              <DatePicker
                id="reprocess-from"
                required
                value={reprocessFrom}
                onChange={(event) => setReprocessFrom(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reprocess-to">To</Label>
              <DatePicker
                id="reprocess-to"
                required
                min={reprocessFrom}
                value={reprocessTo}
                onChange={(event) => setReprocessTo(event.target.value)}
              />
            </div>
            <Button type="submit" disabled={actions.reprocess.isPending}>
              {actions.reprocess.isPending ? 'Processing…' : 'Run now'}
            </Button>
          </form>

          {reprocessed && (
            <p role="status" className="mt-3 rounded-[var(--radius-control)] bg-[var(--color-tint-green)] px-3 py-2 text-sm text-[var(--color-success)]">
              Wrote {reprocessed.daysWritten} day{reprocessed.daysWritten === 1 ? '' : 's'} for{' '}
              {reprocessed.employeesProcessed} employee{reprocessed.employeesProcessed === 1 ? '' : 's'} from{' '}
              {reprocessed.punchesConsumed} punches.
            </p>
          )}
          {actions.reprocess.error != null && <ErrorState error={actions.reprocess.error} />}
        </Card>
      )}

      <nav className="flex gap-1 border-b border-[var(--color-border)]" aria-label="Roster sections">
        {(
          [
            ['assignments', `Assignments${rosters.data ? ` (${rosters.data.totalElements})` : ''}`],
            ['shifts', `Shifts${shifts.data ? ` (${shifts.data.length})` : ''}`],
            ['holidays', 'Holidays'],
          ] as [Tab, string][]
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            aria-current={tab === value ? 'page' : undefined}
            className={cn(
              '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === value
                ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                : 'border-transparent text-[var(--color-body)] hover:text-[var(--color-heading)]',
            )}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* ---------------- Assignments ---------------- */}
      {tab === 'assignments' && (
        <div className="space-y-4">
          <PermissionGate permission="attendance:write">
            <div className="flex justify-end">
              {/* Hidden rather than relabelled while the form is open: the form has its
                  own Cancel, and two buttons reading "Cancel" is one too many. */}
              {!showAssignForm && (
                <Button
                  onClick={() => setShowAssignForm(true)}
                  disabled={shiftList.length === 0}
                  title={shiftList.length === 0 ? 'Add a shift first' : undefined}
                >
                  <Plus aria-hidden="true" />
                  Assign to shift
                </Button>
              )}
            </div>
          </PermissionGate>

          {showAssignForm && (
            <Card className="p-5">
              <h3 className="mb-4 text-base font-semibold text-[var(--color-heading)]">Assign to a shift</h3>
              <AssignRosterForm
                shifts={shiftList}
                submitting={actions.assign.isPending}
                onCancel={() => setShowAssignForm(false)}
                onSubmit={(values) =>
                  actions.assign.mutate(values as unknown as Record<string, unknown>, {
                    onSuccess: () => setShowAssignForm(false),
                  })
                }
              />
              {actions.assign.error != null && <ErrorState error={actions.assign.error} />}
            </Card>
          )}

          <FilterBar
            search={
              <div className="relative">
                <Search
                  className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]"
                  aria-hidden="true"
                />
                <Input
                  aria-label="Search the roster"
                  className="ps-9"
                  placeholder="Search by name or employee code"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value)
                    setPage(0)
                  }}
                />
              </div>
            }
          >
            <Select
              aria-label="Filter by shift"
              value={shiftFilter}
              onChange={(event) => {
                setShiftFilter(event.target.value)
                setPage(0)
              }}
            >
              <option value="">All shifts</option>
              {shiftList.map((shift) => (
                <option key={shift.id} value={shift.id}>
                  {shift.name}
                </option>
              ))}
            </Select>
          </FilterBar>

          <PermissionGate permission="attendance:write">
            <div className="flex justify-end gap-2">
              <Button
                size="sm" variant="outline"
                onClick={() => {
                  void downloadFile(bulkPaths('/v1/attendance/rosters').export, 'rosters.xlsx')
                }}
              >
                <Download aria-hidden="true" />
                Export
              </Button>
              {/* A month of shift assignments is a spreadsheet, not two hundred dialogs. */}
              <Button size="sm" variant="outline" onClick={() => setImportingWhat('rosters')}>
                <Upload aria-hidden="true" />
                Import
              </Button>
            </div>
          </PermissionGate>

          {rosters.isLoading && <Skeleton className="h-64 w-full rounded-[var(--radius-card)]" />}
          {rosters.error != null && <ErrorState error={rosters.error} onRetry={() => void rosters.refetch()} />}

          {rosters.data?.content.length === 0 && (
            <Card>
              <EmptyState
                icon={Users}
                title={debouncedSearch === '' ? 'Nobody is rostered' : 'No matches'}
                description={
                  debouncedSearch === ''
                    ? 'Without an assignment, attendance falls back to the default shift — so late arrivals and week offs will not be judged against the hours people actually work.'
                    : `Nobody on the current roster matches “${debouncedSearch}”.`
                }
                action={
                  shiftList.length === 0 ? (
                    <Button onClick={() => setTab('shifts')}>Add a shift first</Button>
                  ) : (
                    <Button onClick={() => setShowAssignForm(true)}>Assign to shift</Button>
                  )
                }
              />
            </Card>
          )}

          {rosters.data != null && rosters.data.content.length > 0 && (
            <Card className="overflow-x-auto overscroll-x-contain">
              <table className="w-full text-sm">
                <thead>
                  {/* text-start on each cell: a th defaults to centred, and inheriting
                      from the row does not override that. */}
                  <tr className="border-b border-[var(--color-border)] text-xs text-[var(--color-muted)]">
                    <th scope="col" className="px-4 py-3 text-start font-medium">Employee</th>
                    <th scope="col" className="px-4 py-3 text-start font-medium">Shift</th>
                    <th scope="col" className="px-4 py-3 text-start font-medium">From</th>
                    <th scope="col" className="px-4 py-3 text-start font-medium">Until</th>
                    <th scope="col" className="px-4 py-3 text-start font-medium">Week off</th>
                    <th scope="col" className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {rosters.data.content.map((roster) => (
                    <tr key={roster.id} className="border-b border-[var(--color-border)] last:border-b-0">
                      <td className="px-4 py-3">
                        <span className="text-[var(--color-heading)]">{roster.employeeName ?? '—'}</span>
                        <span className="ms-2 font-mono text-xs text-[var(--color-muted)]">
                          {roster.employeeCode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[var(--color-body)]">{roster.shiftName ?? '—'}</td>
                      <td className="px-4 py-3 text-[var(--color-body)]">
                        <DateValue value={roster.fromDate} />
                      </td>
                      <td className="px-4 py-3 text-[var(--color-body)]">
                        {roster.toDate ? <DateValue value={roster.toDate} /> : 'Open-ended'}
                      </td>
                      <td className="px-4 py-3 text-[var(--color-body)]">
                        {roster.weekOffDays.map(shortDayName).join(', ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-end">
                        <PermissionGate permission="attendance:write">
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Remove ${roster.employeeName ?? 'assignment'}`}
                            onClick={() => actions.remove.mutate(roster.id)}
                          >
                            <Trash2 aria-hidden="true" />
                          </Button>
                        </PermissionGate>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {rosters.data.totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] px-4 py-3 text-sm">
                  <span className="text-[var(--color-muted)]">
                    Page {rosters.data.page + 1} of {rosters.data.totalPages} ·{' '}
                    {rosters.data.totalElements} assignment
                    {rosters.data.totalElements === 1 ? '' : 's'}
                  </span>
                  <span className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={rosters.data.first}
                      onClick={() => setPage((current) => Math.max(current - 1, 0))}
                    >
                      <ChevronLeft aria-hidden="true" />
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={rosters.data.last}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Next
                      <ChevronRight aria-hidden="true" />
                    </Button>
                  </span>
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* ---------------- Shifts ---------------- */}
      {tab === 'shifts' && (
        <div className="space-y-4">
          <PermissionGate permission="attendance:write">
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  void downloadFile(bulkPaths('/v1/attendance/shifts').export, 'shifts.xlsx')
                }}
              >
                <Download aria-hidden="true" />
                Export
              </Button>
              <Button variant="outline" onClick={() => setImportingWhat('shifts')}>
                <Upload aria-hidden="true" />
                Import
              </Button>
              {!showShiftForm && !editingShift && (
                <Button onClick={() => setShowShiftForm(true)}>
                  <Plus aria-hidden="true" />
                  Add shift
                </Button>
              )}
            </div>
          </PermissionGate>

          {(showShiftForm || editingShift) && (
            <Card className="p-5">
              <h3 className="mb-4 text-base font-semibold text-[var(--color-heading)]">
                {editingShift ? `Edit ${editingShift.name}` : 'New shift'}
              </h3>
              <ShiftForm
                key={editingShift?.id ?? 'new'}
                submitting={actions.createShift.isPending || actions.updateShift.isPending}
                initial={editingShift
                  ? {
                      code: editingShift.code,
                      name: editingShift.name,
                      startTime: editingShift.startTime.slice(0, 5),
                      endTime: editingShift.endTime.slice(0, 5),
                      graceInMinutes: editingShift.graceInMinutes,
                      breakMinutes: editingShift.breakMinutes,
                      fullDayMinutes: editingShift.fullDayMinutes,
                      // Not on the list view, so an edit keeps whatever the shift already has
                      // rather than resetting it to a number nobody chose.
                      halfDayMinutes: Math.min(240, editingShift.fullDayMinutes),
                    }
                  : undefined}
                onCancel={() => { setShowShiftForm(false); setEditingShift(null) }}
                onSubmit={(values) => {
                  const body = values as unknown as Record<string, unknown>
                  if (editingShift) {
                    actions.updateShift.mutate({ id: editingShift.id, ...body }, {
                      onSuccess: () => setEditingShift(null),
                    })
                  } else {
                    actions.createShift.mutate(body, {
                      onSuccess: () => setShowShiftForm(false),
                    })
                  }
                }}
              />
              {actions.createShift.error != null && <ErrorState error={actions.createShift.error} />}
              {actions.updateShift.error != null && <ErrorState error={actions.updateShift.error} />}
            </Card>
          )}

          {shifts.isLoading && <Skeleton className="h-40 w-full rounded-[var(--radius-card)]" />}
          {shifts.error != null && <ErrorState error={shifts.error} onRetry={() => void shifts.refetch()} />}

          {/* Only once the list has arrived: while it loads the list is empty too, and the
              screen said "No shifts defined" — with a second "Add shift" — over the skeleton. */}
          {shifts.data != null && shiftList.length === 0 && !showShiftForm && (
            <Card>
              <EmptyState
                icon={Clock}
                title="No shifts defined"
                description="A shift says when the day starts, how much grace an arrival gets, and when extra hours become overtime."
                action={<Button onClick={() => setShowShiftForm(true)}>Add shift</Button>}
              />
            </Card>
          )}

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {shiftList.map((shift) => (
              <Card key={shift.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-[var(--color-heading)]">{shift.name}</p>
                    <p className="font-mono text-xs text-[var(--color-muted)]">{shift.code}</p>
                  </div>
                  {shift.crossesMidnight && (
                    <span className="shrink-0 rounded-full bg-[var(--color-tint-orange)] px-2 py-0.5 text-xs text-[var(--color-warning)]">
                      Overnight
                    </span>
                  )}
                </div>

                <p className="mt-3 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
                  {shift.startTime.slice(0, 5)} – {shift.endTime.slice(0, 5)}
                </p>

                <dl className="mt-3 space-y-1 text-xs">
                  <div className="flex justify-between gap-2">
                    <dt className="text-[var(--color-muted)]">Grace on arrival</dt>
                    <dd className="tabular-nums text-[var(--color-heading)]">{shift.graceInMinutes} min</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-[var(--color-muted)]">Unpaid break</dt>
                    <dd className="tabular-nums text-[var(--color-heading)]">{shift.breakMinutes} min</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-[var(--color-muted)]">Full day at</dt>
                    <dd className="tabular-nums text-[var(--color-heading)]">{hours(shift.fullDayMinutes)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-[var(--color-muted)]">Assigned</dt>
                    <dd className="tabular-nums text-[var(--color-heading)]">{shift.assignedCount}</dd>
                  </div>
                </dl>

                <PermissionGate permission="attendance:write">
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm" variant="ghost"
                      aria-label={`Edit ${shift.name}`}
                      onClick={() => { setShowShiftForm(false); setEditingShift(shift) }}
                    >
                      <Pencil aria-hidden="true" />
                      Edit
                    </Button>
                    {shift.active && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setActionError(null); setRemovingShift(shift) }}
                      >
                        Deactivate
                      </Button>
                    )}
                  </div>
                </PermissionGate>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- Holidays ---------------- */}
      {tab === 'holidays' && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="holiday-year">Year</Label>
              <Input
                id="holiday-year"
                type="number"
                min={2000}
                max={2100}
                className="w-32"
                value={holidayYear}
                onChange={(event) => {
                  const value = Number(event.target.value)
                  if (value >= 2000 && value <= 2100) setHolidayYear(value)
                }}
              />
            </div>
          <PermissionGate permission="attendance:write">
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  void downloadFile(bulkPaths('/v1/attendance/holidays').export, 'holidays.xlsx')
                }}
              >
                <Download aria-hidden="true" />
                Export
              </Button>
              <Button variant="outline" onClick={() => setImportingWhat('holidays')}>
                <Upload aria-hidden="true" />
                Import
              </Button>
              <Button
                onClick={() => {
                  setActionError(null)
                  setHolidayDraft({
                    holidayDate: '', name: '', locationId: '', optional: false,
                  })
                }}
              >
                <Plus aria-hidden="true" />
                Add holiday
              </Button>
            </div>
          </PermissionGate>
          </div>

          <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] p-3 text-sm text-[var(--color-muted)]">
            <PartyPopper className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Nobody types a year of public holidays one at a time — import the published list.
            A day with no punches and no holiday is recorded as an absence, so this calendar
            decides as much as the roster does.
          </p>

          {holidays.isLoading && <Skeleton className="h-40 w-full rounded-[var(--radius-card)]" />}
          {holidays.error != null && <ErrorState error={holidays.error} onRetry={() => void holidays.refetch()} />}
          {holidays.data?.length === 0 && (
            <Card>
              <EmptyState
                icon={PartyPopper}
                title={`No holidays in ${holidayYear}`}
                description="A day with no punches and no holiday is recorded as an absence, so the calendar matters as much as the roster."
              />
            </Card>
          )}
          {holidays.data != null && holidays.data.length > 0 && (
            <Card className="divide-y divide-[var(--color-border)]">
              {holidays.data.map((holiday) => (
                <div key={holiday.id} className="flex items-center gap-4 px-4 py-3">
                  <CalendarClock className="size-4 shrink-0 text-[var(--color-primary)]" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--color-heading)]">
                    {holiday.name}
                  </span>
                  {holiday.optional && (
                    <span className="shrink-0 text-xs text-[var(--color-muted)]">Optional</span>
                  )}
                  {holiday.locationName && (
                    <span className="shrink-0 text-xs text-[var(--color-muted)]">
                      {holiday.locationName} only
                    </span>
                  )}
                  <span className="shrink-0 text-sm text-[var(--color-body)]">
                    <DateValue value={holiday.holidayDate} />
                  </span>
                  <PermissionGate permission="attendance:write">
                    <span className="flex shrink-0 gap-1">
                      <Button
                        size="sm" variant="ghost"
                        aria-label={`Edit ${holiday.name}`}
                        onClick={() => {
                          setActionError(null)
                          setHolidayDraft({
                            id: holiday.id,
                            holidayDate: holiday.holidayDate,
                            name: holiday.name,
                            locationId: holiday.locationId ?? '',
                            optional: holiday.optional,
                          })
                        }}
                      >
                        <Pencil aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        aria-label={`Remove ${holiday.name}`}
                        onClick={() => { setActionError(null); setRemovingHoliday(holiday) }}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    </span>
                  </PermissionGate>
                </div>
              ))}
            </Card>
          )}
        </>
      )}

      {/* ---------------- dialogs shared by the tabs ---------------- */}

      <ImportDialog
        open={importingWhat !== null}
        onOpenChange={(open) => { if (!open) setImportingWhat(null) }}
        what={importingWhat ?? 'rows'}
        prefix={`/v1/attendance/${importingWhat ?? 'shifts'}`}
        invalidateKey={['attendance']}
      />

      <ConfirmDialog
        open={removingShift !== null}
        onOpenChange={(open) => { if (!open) setRemovingShift(null) }}
        title={`Deactivate ${removingShift?.name ?? 'this shift'}?`}
        description="It stops being offered for new rosters and stays on file, so attendance already judged against it still explains itself."
        confirmLabel="Deactivate"
        loading={actions.deactivateShift.isPending}
        error={actionError}
        onConfirm={() => {
          if (!removingShift) return
          setActionError(null)
          actions.deactivateShift.mutate(removingShift.id, {
            onSuccess: () => setRemovingShift(null),
            onError: (caught) => setActionError(
              toApiProblem(caught)?.detail ?? 'That shift could not be deactivated.'),
          })
        }}
      />

      <ConfirmDialog
        open={removingHoliday !== null}
        onOpenChange={(open) => { if (!open) setRemovingHoliday(null) }}
        title={`Take ${removingHoliday?.name ?? 'this holiday'} off the calendar?`}
        description="Days already processed as a holiday keep saying so — the row is kept, it just stops applying from now on."
        confirmLabel="Remove it"
        loading={holidayActions.remove.isPending}
        error={actionError}
        onConfirm={() => {
          if (!removingHoliday) return
          setActionError(null)
          holidayActions.remove.mutate(removingHoliday.id, {
            onSuccess: () => setRemovingHoliday(null),
            onError: (caught) => setActionError(
              toApiProblem(caught)?.detail ?? 'That holiday could not be removed.'),
          })
        }}
      />

      <FormDialog
        open={holidayDraft !== null}
        onOpenChange={(open) => { if (!open) setHolidayDraft(null) }}
        title={holidayDraft?.id ? 'Edit holiday' : 'Add holiday'}
        description="A company-wide holiday, or one only a single site observes."
        submitLabel="Save holiday"
        loading={holidayActions.save.isPending}
        error={actionError}
        onSubmit={() => {
          if (!holidayDraft) return
          setActionError(null)
          holidayActions.save.mutate({
            id: holidayDraft.id,
            holidayDate: holidayDraft.holidayDate,
            name: holidayDraft.name,
            locationId: holidayDraft.locationId || null,
            optional: holidayDraft.optional,
          }, {
            onSuccess: () => setHolidayDraft(null),
            onError: (caught) => setActionError(
              toApiProblem(caught)?.detail ?? 'That holiday could not be saved.'),
          })
        }}
      >
        {holidayDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="holiday-date">Date</Label>
              <DatePicker
                id="holiday-date"
                value={holidayDraft.holidayDate}
                onChange={(event) =>
                  setHolidayDraft({ ...holidayDraft, holidayDate: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="holiday-name">Name</Label>
              <Input
                id="holiday-name"
                value={holidayDraft.name}
                onChange={(event) =>
                  setHolidayDraft({ ...holidayDraft, name: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="holiday-location">Location</Label>
              <Select
                id="holiday-location"
                className="w-full"
                value={holidayDraft.locationId}
                onChange={(event) =>
                  setHolidayDraft({ ...holidayDraft, locationId: event.target.value })}
              >
                <option value="">The whole company</option>
                {locations.data?.map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </Select>
            </div>
            <label className="flex items-start gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={holidayDraft.optional}
                onChange={(event) =>
                  setHolidayDraft({ ...holidayDraft, optional: event.target.checked })}
              />
              <span>
                Optional — a day people may take but need not. It stays a working day unless
                leave is booked, which is the difference between "the office was shut" and
                "you could have stayed home".
              </span>
            </label>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
